import { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/FirebaseConfig";
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../../firebase/FirebaseConfig";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

type Exec = {
  name: string;
  position: string;
  positionDescription: string;
  favSong: string;
  imageURL: string;
};

export default function AddChangeDelExecBoard() {
  const [execs, setExecs] = useState<Exec[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newExec, setNewExec] = useState<Exec>({
    name: "",
    position: "",
    positionDescription: "",
    favSong: "",
    imageURL: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);

  const docRef = doc(db, "exec", "execData");

  const handleClickOpenDelete = (index: number) => {
    setDeleteIndex(index);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteIndex(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteIndex === null) return;

    const execToDelete = execs[deleteIndex];

    // 🔥 Delete the image from Firebase Storage
    if (execToDelete.imageURL) {
      try {
        const imageRef = ref(storage, execToDelete.imageURL); // ✅ MUST be relative path like "ExecImgs/Filename.png"
        await deleteObject(imageRef);
        console.log("✅ Image deleted:", execToDelete.imageURL);
      } catch (error) {
        console.error("❌ Failed to delete image:", error);
      }
    }

    // ✂️ Remove from Firestore
    const updated = execs.filter((_, i) => i !== deleteIndex);
    await updateDoc(doc(db, "exec", "execData"), {
      execBoard: updated,
    });

    setExecs(updated);
    setImagePreviews((prev) => prev.filter((_, i) => i !== deleteIndex));
    setDeleteIndex(null);
    setOpenDelete(false);
  };

  const handleSave = async (index: number) => {
    setEditIndex(null);
    await saveExecBoard(execs);
  };

  const handleImageUpload = async (file: File, index: number) => {
    const fileName = file.name;
    const storagePath = `ExecImgs/${fileName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);

    const updated = [...execs];
    updated[index].imageURL = storagePath;
    setExecs(updated);

    const url = await getDownloadURL(storageRef);
    const previews = [...imagePreviews];
    previews[index] = url;
    setImagePreviews(previews);
  };

  const loadImagePreviews = async (execList: Exec[]) => {
    const urls = await Promise.all(
      execList.map(async (exec) => {
        if (!exec.imageURL) return "";
        try {
          const imgRef = ref(storage, exec.imageURL);
          return await getDownloadURL(imgRef);
        } catch {
          return "";
        }
      })
    );
    setImagePreviews(urls);
  };

  const saveExecBoard = async (updatedBoard: Exec[]) => {
    await updateDoc(docRef, {
      execBoard: updatedBoard,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        const execList = data.execBoard || [];
        setExecs(execList);
        loadImagePreviews(execList);
      }
    };
    fetchData();
  }, []);

  const handleAddExec = async () => {
    let imagePath = "";
    let previewUrl = "";

    if (imageFile) {
      imagePath = `ExecImgs/${imageFile.name}`;
      const storageRef = ref(storage, imagePath);
      await uploadBytes(storageRef, imageFile);
      previewUrl = await getDownloadURL(storageRef);
    }

    const newEntry: Exec = {
      ...newExec,
      imageURL: imagePath,
    };

    const updatedExecs = [...execs, newEntry];
    await saveExecBoard(updatedExecs);

    setExecs(updatedExecs);
    setImagePreviews((prev) => [...prev, previewUrl]);
    setNewExec({
      name: "",
      position: "",
      positionDescription: "",
      favSong: "",
      imageURL: "",
    });
    setImageFile(null);
    setOpenAddDialog(false);
  };

  return (
    <>
      <div
        className="faq"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex" }}>
          <h1 style={{ marginRight: 20 }}>ADD/EDIT/DELETE EXEC BOARD</h1>
          <Button variant="outlined" onClick={() => setOpenAddDialog(true)}>
            <AddIcon></AddIcon>
          </Button>
        </div>

        {execs.map((exec, index) => (
          <Card key={index} sx={{ width: 230, p: 2 }}>
            <CardContent>
              {editIndex === index ? (
                <>
                  <TextField
                    fullWidth
                    label="Name"
                    value={exec.name}
                    onChange={(e) => {
                      const updated = [...execs];
                      updated[index].name = e.target.value;
                      setExecs(updated);
                    }}
                    size="small"
                    style={{ paddingBottom: 20 }}
                  />
                  <TextField
                    fullWidth
                    label="Position"
                    value={exec.position}
                    onChange={(e) => {
                      const updated = [...execs];
                      updated[index].position = e.target.value;
                      setExecs(updated);
                    }}
                    size="small"
                    style={{ paddingBottom: 20 }}
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    value={exec.positionDescription}
                    onChange={(e) => {
                      const updated = [...execs];
                      updated[index].positionDescription = e.target.value;
                      setExecs(updated);
                    }}
                    size="small"
                    style={{ paddingBottom: 20 }}
                  />
                  <TextField
                    fullWidth
                    label="Fav Song"
                    value={exec.favSong}
                    onChange={(e) => {
                      const updated = [...execs];
                      updated[index].favSong = e.target.value;
                      setExecs(updated);
                    }}
                    size="small"
                    style={{ paddingBottom: 20 }}
                  />
                  {imagePreviews[index] && (
                    <img
                      src={imagePreviews[index]}
                      alt={exec.name}
                      style={{
                        width: "100%",
                        borderRadius: 8,
                        marginBottom: 8,
                      }}
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, index);
                    }}
                  />
                  <Typography variant="caption">
                    Stored at: {exec.imageURL}
                  </Typography>
                  <Button onClick={() => handleSave(index)}>Save</Button>
                </>
              ) : (
                <>
                  <Typography variant="h6">{exec.name}</Typography>
                  <Typography>{exec.position}</Typography>
                  {imagePreviews[index] && (
                    <img
                      src={imagePreviews[index]}
                      alt={exec.name}
                      style={{
                        width: "100%",
                        borderRadius: 8,
                        marginBottom: 8,
                      }}
                    />
                  )}
                  <Typography variant="body2">
                    {exec.positionDescription}
                  </Typography>
                  <Typography variant="caption">{exec.favSong}</Typography>
                  <br />
                  <Button size="small" onClick={() => setEditIndex(index)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleClickOpenDelete(index)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Delete Dialog */}
      <BootstrapDialog
        onClose={handleCloseDelete}
        open={openDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          {deleteIndex !== null && execs[deleteIndex]
            ? `Delete ${execs[deleteIndex].name}?`
            : "Delete Exec?"}
          <IconButton
            onClick={handleCloseDelete}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          Are you sure you want to permanently remove this exec member?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </BootstrapDialog>

      {/* Add Exec Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>
          Add Exec Member
          <IconButton
            onClick={() => setOpenAddDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Name"
            value={newExec.name}
            onChange={(e) => setNewExec({ ...newExec, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Position"
            value={newExec.position}
            onChange={(e) =>
              setNewExec({ ...newExec, position: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Position Description"
            value={newExec.positionDescription}
            onChange={(e) =>
              setNewExec({
                ...newExec,
                positionDescription: e.target.value,
              })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Favorite Song"
            value={newExec.favSong}
            onChange={(e) =>
              setNewExec({ ...newExec, favSong: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setImageFile(file);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddExec} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
